import { useEffect, useRef, useState } from "react";
import cx from "classnames";
import getFeature from "@template/helpers/getFeature";
import trackExperimentEvent from "@src/client/analytics/optimizely/trackExperimentEvent";
import { noticeError } from "@src/helpers/monitoring";
import styles from "./index.css";

const CAMPAIGN_FEATURE = "native_app_banner_campaign";
const CAMPAIGN_DECISION_TIMEOUT = 500;
const CLICK_EVENTS = {
  getApp: "native_app_banner_get_app_click",
  viewInApp: "native_app_banner_view_in_app_click",
  close: "native_app_banner_close_click"
};
const THEMES = {
  black: {
    className: styles.campaign,
    getAppTheme: "green"
  },
  blue: {
    className: cx(styles.campaign, styles.britishBlue),
    getAppTheme: "secondary",
    outlineViewInApp: true
  }
};

const useNativeAppBannerCampaign = ({ isBannerDisplayed, osPlatform }) => {
  const [campaign, setCampaign] = useState(null);
  const campaignFeature = useRef(null);
  const isExposureRecorded = useRef(false);

  useEffect(() => {
    let isActive = true;
    const timeout = new Promise(resolve =>
      setTimeout(resolve, CAMPAIGN_DECISION_TIMEOUT)
    );

    Promise.race([getFeature(CAMPAIGN_FEATURE), timeout])
      .then(feature => {
        campaignFeature.current = feature;
        return feature?.getVariables() ?? {};
      })
      .catch(error => {
        noticeError(error, { context: "nativeAppBannerCampaign" });
        return {};
      })
      .then(variables => isActive && setCampaign(variables));

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (campaign && isBannerDisplayed && !isExposureRecorded.current) {
      isExposureRecorded.current = true;
      campaignFeature.current?.isEnabled();
    }
  }, [isBannerDisplayed, campaign]);

  const trackClick = action => {
    const eventKey = CLICK_EVENTS[action];
    return trackExperimentEvent(eventKey, { platform: osPlatform }).catch(
      error =>
        noticeError(error, { context: "nativeAppBannerTracking", eventKey })
    );
  };

  const theme = Object.keys(THEMES).includes(campaign?.theme)
    ? THEMES[campaign.theme]
    : undefined;

  return {
    theme,
    showCampaignText: campaign?.showCampaignText === true,
    isCampaignReady: !!campaign,
    trackClick
  };
};

export default useNativeAppBannerCampaign;
